import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useInfiniteQuery } from "@tanstack/react-query";
import { searchSchema } from "@/domain/search/search.schema";
import { z } from "zod/v4";
import { ChefCardSkeleton } from "@/components/ChefCardSkeleton";
import { ChefLiveCard } from "@/components/marketplace/ChefLiveCard";
import { EmptyState } from "@/components/marketplace/EmptyState";
import { useInView } from "react-intersection-observer";
import { useEffect, useMemo, memo, lazy, Suspense, Fragment } from "react";
import { chefQueries } from "@/domain/chef/chef.query";
import { createSEOHead, SEO_DATA, createFindChefCitySEO } from "@/lib/seo";

const CustomRequestCta = lazy(() =>
  import("@/components/layouts/CustomRequestCta").then((module) => ({
    default: module.CustomRequestCta,
  })),
);

export const Route = createFileRoute("/(public)/trouver-un-chef/chefs")({
  validateSearch: (search) => {
    const result = searchSchema.safeParse(search);
    if (!result.success) {
      const validFields: Record<string, any> = {};
      Object.keys(search || {}).forEach((key) => {
        const fieldResult = searchSchema.shape[
          key as keyof typeof searchSchema.shape
        ]?.safeParse(search[key]);
        if (fieldResult?.success) {
          validFields[key] = fieldResult.data;
        }
      });
      return validFields as z.infer<typeof searchSchema>;
    }
    return result.data;
  },
  loader: ({ context: { queryClient }, deps }) => {
    return queryClient.ensureQueryData(chefQueries.findChefs(deps));
  },
  head: ({ match }) => {
    if (match.search.city) {
      return createSEOHead(createFindChefCitySEO(match.search.city));
    }
    return createSEOHead(SEO_DATA.FIND_CHEF);
  },
  component: RouteComponent,
});

const InitialSkeletons = memo(() => {
  const skeletons = useMemo(
    () => Array.from({ length: 6 }, (_, i) => <ChefCardSkeleton key={i} />),
    [],
  );

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {skeletons}
    </div>
  );
});

const LoadingSkeletons = memo(() => {
  const skeletons = useMemo(
    () =>
      Array.from({ length: 3 }, (_, i) => (
        <ChefCardSkeleton key={`loading-${i}`} />
      )),
    [],
  );

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {skeletons}
    </div>
  );
});

function RouteComponent() {
  const search = Route.useSearch();
  const navigate = useNavigate();
  const { ref, inView } = useInView({
    threshold: 0,
    rootMargin: "100px",
  });

  const {
    data,
    status,
    error,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery(chefQueries.findChefs(search));

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const totalChefs = useMemo(
    () =>
      data?.pages.reduce((acc, page) => acc + (page.data?.length ?? 0), 0) ?? 0,
    [data?.pages],
  );

  if (status === "pending") {
    return (
      <section className="container mx-auto px-4 py-6 max-w-6xl">
        <InitialSkeletons />
      </section>
    );
  }

  if (status === "error") {
    return (
      <section className="container mx-auto px-4 py-6 max-w-6xl">
        <div className="bg-white rounded-3xl border border-red-200 p-10 text-center">
          <div role="alert" className="text-red-600 font-medium">
            Une erreur est survenue : {error?.message}
          </div>
        </div>
      </section>
    );
  }

  if (totalChefs === 0) {
    return (
      <section className="container mx-auto px-4 py-6 max-w-6xl">
        <EmptyState
          onReset={() =>
            navigate({
              to: "/trouver-un-chef/chefs",
              search: {},
            })
          }
        />
      </section>
    );
  }

  return (
    <section className="container mx-auto px-4 py-6 max-w-6xl space-y-8">
      {data?.pages.map((page, pageIndex) => {
        let chefIndexOffset = 0;
        for (let p = 0; p < pageIndex; p++) {
          chefIndexOffset += data.pages[p].data.length;
        }

        return (
          <Fragment key={`page-${pageIndex}`}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {page.data.map((chef, chefIndex) => (
                <ChefLiveCard
                  key={chef.id}
                  chef={chef}
                  index={chefIndexOffset + chefIndex}
                  isLCP={pageIndex === 0 && chefIndex === 0}
                />
              ))}
            </div>

            {pageIndex === 0 && page.data.length > 0 && (
              <Suspense fallback={null}>
                <CustomRequestCta />
              </Suspense>
            )}
          </Fragment>
        );
      })}

      {hasNextPage && (
        <div ref={ref} className="flex justify-center py-4">
          {isFetchingNextPage ? <LoadingSkeletons /> : <div className="h-10" />}
        </div>
      )}
    </section>
  );
}
